/**
 * OnboardingStore — instrumentação de duração do onboarding (HLOS-46).
 *
 * Track tempo entre primeiro turno do onboarding (markStart) e conclusão
 * (markComplete). Expõe durationMs para audit + smoke test CI.
 *
 * Constitucional:
 * - AC5-S4 da REQ-006: onboarding ≤ 5min (D7 / ADR-0014)
 * - CL-09 (Explicabilidade): rastro de duração visível em logs
 * - CC-04 (sem urgência artificial): zero fake-progress; mede tempo real
 */

import { defineStore } from "pinia";
import { computed, ref } from "vue";

/** Limite superior do gate (5min em ms). */
export const ONBOARDING_LIMIT_MS = 5 * 60 * 1000;

export const useOnboardingStore = defineStore("onboarding", () => {
  const startedAt = ref<number | null>(null);
  const completedAt = ref<number | null>(null);

  /**
   * Marca início do onboarding. Chamado em onMounted do OnboardingState.
   * Idempotente: se já started, não reseta.
   */
  function markStart(): void {
    if (startedAt.value === null) {
      startedAt.value = Date.now();
    }
  }

  /**
   * Marca conclusão do onboarding. Chamado em onOnboardingComplete.
   * Calcula durationMs no momento da conclusão.
   */
  function markComplete(): void {
    if (startedAt.value === null) return;
    completedAt.value = Date.now();
  }

  /** Duração em ms. null enquanto onboarding não concluído. */
  const durationMs = computed<number | null>(() => {
    if (startedAt.value === null) return null;
    const end = completedAt.value ?? Date.now();
    return end - startedAt.value;
  });

  /** Reset (útil para testes). */
  function reset(): void {
    startedAt.value = null;
    completedAt.value = null;
  }

  return { startedAt, completedAt, durationMs, markStart, markComplete, reset };
});
